import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { Container } from '../../components/content';
import ProductForm from '../../components/products/ProductForm';
import DeleteModal from '../../components/modals/DeleteModal';
import {
  createProduct,
  fetchProductFilters,
  saveProductCommissionChain,
  saveProductAgentCommission,
  saveProductCashbackOverride,
  fetchCurrentCheckoutUpsellProduct,
  updateProductCheckoutUpsell,
} from '../../api/webshop-products-orders-api';
import styles from '../../styles/mobileInfoPage.module.scss';

export default function DodajProizvod() {
  const navigate = useNavigate();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [upsellConflictOpen, setUpsellConflictOpen] = useState(false);
  const [upsellConflictProductName, setUpsellConflictProductName] = useState('');
  const [pendingSubmitArgs, setPendingSubmitArgs] = useState(null);

  useEffect(() => {
    fetchProductFilters().then(({ data, error }) => {
      if (!error) setCategoryOptions(data?.categories || []);
    });
  }, []);

  const performSave = async (payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload) => {
    setSaving(true);
    const { data, success, message } = await createProduct(payload);
    if (!success) {
      toast.error(message || 'Greška pri kreiranju proizvoda');
      setSaving(false);
      return;
    }

    // The commission chain/agent commission and checkout-upsell flag can only be
    // saved once the product exists (they need its _id).
    if (commissionChainPayload) {
      const chainResult = await saveProductCommissionChain(data._id, commissionChainPayload);
      if (!chainResult.success) {
        toast.error(
          chainResult.message ||
            'Proizvod je kreiran, ali provizija mentorskog lanca nije sačuvana'
        );
        setSaving(false);
        return;
      }
    }

    if (agentCommissionPayload) {
      const agentResult = await saveProductAgentCommission(data._id, agentCommissionPayload);
      if (!agentResult.success) {
        toast.error(
          agentResult.message || 'Proizvod je kreiran, ali provizija za agente nije sačuvana'
        );
        setSaving(false);
        return;
      }
    }

    if (checkoutUpsellPayload.isCheckoutUpsell) {
      const upsellResult = await updateProductCheckoutUpsell(
        data._id,
        checkoutUpsellPayload.isCheckoutUpsell,
        checkoutUpsellPayload.checkoutUpsellDiscountPercent
      );
      if (!upsellResult.success) {
        toast.error(
          upsellResult.message ||
            'Proizvod je kreiran, ali ponuda za dodatnu kupovinu nije sačuvana'
        );
        setSaving(false);
        return;
      }
    }

    if (cashbackOverridePayload) {
      const cashbackResult = await saveProductCashbackOverride(data._id, cashbackOverridePayload);
      if (!cashbackResult.success) {
        toast.error(
          cashbackResult.message || 'Proizvod je kreiran, ali cashback podešavanje nije sačuvano'
        );
        setSaving(false);
        return;
      }
    }

    toast.success('Proizvod je uspešno kreiran');
    navigate('/proizvodi');
    setSaving(false);
  };

  const handleSubmit = async (payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload) => {
    if (checkoutUpsellPayload.isCheckoutUpsell) {
      const { data: current } = await fetchCurrentCheckoutUpsellProduct();
      if (current) {
        setUpsellConflictProductName(current.name);
        setPendingSubmitArgs([payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload]);
        setUpsellConflictOpen(true);
        return;
      }
    }
    await performSave(payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload);
  };

  const handleConfirmUpsellConflict = async () => {
    setUpsellConflictOpen(false);
    if (pendingSubmitArgs) {
      await performSave(...pendingSubmitArgs);
      setPendingSubmitArgs(null);
    }
  };

  return (
    <Container>
      <Link to="/proizvodi" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <h2 className={styles.pageTitle} style={{ marginBottom: '32px' }}>
          Novi proizvod
        </h2>
        <ProductForm
          mode="create"
          categoryOptions={categoryOptions}
          saving={saving}
          onSubmit={handleSubmit}
        />
      </ContentLayout>

      <DeleteModal
        visible={upsellConflictOpen}
        onDelete={handleConfirmUpsellConflict}
        onCancel={() => {
          setUpsellConflictOpen(false);
          setPendingSubmitArgs(null);
        }}
        loading={saving}
        title="Zamena ponude za dodatnu kupovinu"
        message={`Proizvod "${upsellConflictProductName}" je trenutno podešen kao ponuda za dodatnu kupovinu na checkout-u. Ako nastavite, ta ponuda će biti uklonjena sa tog proizvoda. Da li ste sigurni?`}
        buttonText="Da, zameni"
        cancelText="Odustani"
      />
    </Container>
  );
}
